import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Switch, Space, ConfigProvider, theme, Button as AntdButton, Select as AntdSelect } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { message } from "antd";
import { getGradesHorarias, getGradeHorariaById, saveGradeHoraria } from "@/services/gradeHoraria";
import { getUnidades } from "@/services/unidade";
import { getTurma } from "@/services/turma";
import { usePermissions } from "@/hooks/usePermissions";

const MENU_ID = 19;

export default function ConsultaGradeHoraria() {
  const [grades, setGrades] = useState<any[]>([]);
  const [unidades, setUnidades] = useState<any[]>([]);
  const [turmas, setTurmas] = useState<any[]>([]);
  const [filtroUnidadeId, setFiltroUnidadeId] = useState<number | undefined>();
  const [filtroTurmaId, setFiltroTurmaId] = useState<number | undefined>();
  const navigate = useNavigate();
  const { canInsert, canEdit } = usePermissions();

  const loadGrades = useCallback(
    (turmaId?: number, unidadeId?: number) => {
      getGradesHorarias({ turmaId, unidadeId }).then((res) => {
        if (res.status === 200) setGrades(res.data);
        else setGrades([]);
      });
    },
    []
  );

  useEffect(() => {
    loadGrades();
    getUnidades().then((res) => {
      if (res.status === 200) setUnidades(res.data.filter((u: any) => u.ativo));
    });
    getTurma().then((res) => {
      if (res.status === 200) setTurmas(res.data.filter((t: any) => t.ativo));
    });
  }, [loadGrades]);

  const handleFilter = useCallback(() => {
    loadGrades(filtroTurmaId, filtroUnidadeId);
  }, [loadGrades, filtroTurmaId, filtroUnidadeId]);

  useEffect(() => {
    handleFilter();
  }, [filtroUnidadeId, filtroTurmaId]);

  const toggleAtivo = async (id: number) => {
    try {
      const { data: grade } = await getGradeHorariaById(id);
      const updated = { ...grade, ativo: !grade.ativo };
      const res = await saveGradeHoraria(updated);
      if (res.status === 200 || res.status === 201) {
        setGrades((prev) =>
          prev.map((g) => (g.id === id ? { ...g, ativo: !g.ativo } : g))
        );
        message.success(`Grade ${!grade.ativo ? "ativada" : "inativada"} com sucesso!`);
      } else {
        message.error("Falha ao atualizar o status da grade.");
      }
    } catch {
      message.error("Erro ao comunicar com o servidor.");
    }
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: "Unidade",
      dataIndex: "unidadeNome",
      key: "unidadeNome",
      sorter: (a: any, b: any) => (a.unidadeNome ?? "").localeCompare(b.unidadeNome ?? ""),
    },
    {
      title: "Turma",
      dataIndex: "turmaNome",
      key: "turmaNome",
      sorter: (a: any, b: any) => (a.turmaNome ?? "").localeCompare(b.turmaNome ?? ""),
    },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      render: (value: boolean, record: any) => (
        <Switch
          checked={!!value}
          onChange={() => toggleAtivo(record.id)}
          checkedChildren="Ativo"
          unCheckedChildren="Inativo"
        />
      ),
      sorter: (a: any, b: any) => Number(b.ativo) - Number(a.ativo),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_: any, record: any) => (
        <Space size="middle">
          {canEdit(MENU_ID) && (
            <AntdButton
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(`/gradehoraria/${record.id}`)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Consulta de Grades Horárias</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-4 items-end mb-8 flex-wrap">
          <div className="flex flex-col gap-1">
            <span className="text-white text-sm">Unidade</span>
            <AntdSelect
              allowClear
              placeholder="Todas as unidades"
              style={{ width: 220 }}
              onChange={(val) => setFiltroUnidadeId(val)}
              options={unidades.map((u) => ({ value: u.id, label: u.nome }))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-white text-sm">Turma</span>
            <AntdSelect
              allowClear
              placeholder="Todas as turmas"
              style={{ width: 220 }}
              onChange={(val) => setFiltroTurmaId(val)}
              options={turmas.map((t) => ({ value: t.id, label: t.nome }))}
            />
          </div>
          {canInsert(MENU_ID) && (
            <AntdButton
              className="flex gap-2"
              onClick={() => navigate("/gradehoraria/cadastro")}
            >
              Nova Grade
              <PlusOutlined />
            </AntdButton>
          )}
        </div>
        <div className="grid">
          <Table columns={columns} dataSource={grades} rowKey="id" />
        </div>
      </ConfigProvider>
    </>
  );
}
